import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Home,
  Phone,
  Mail,
  MapPin,
  Calendar,
  ChevronRight,
  User
} from "lucide-react";
import { Button } from "../components/ui/button";

const advisors = [
  {
    id: "zahirul",
    name: "Zahirul Bhuyain",
    title: "Senior Mortgage & Protection Adviser",
    specialties: ["Residential Mortgages", "Commercial Mortgages", "Buy-to-Let", "Protection"],
    image: "https://customer-assets.emergentagent.com/job_253ab87a-e10d-46f5-9b4d-bd4ec5274312/artifacts/6o53gkj1_zahirul%202bhuyain.jpg.jpeg",
    phone: "07550199399",
    email: "zahir@thamesresi.com",
    bio: "Helping families and investors achieve their property dreams with expert mortgage advice. FCA regulated, dedicated to finding the best deals for you."
  },
  {
    id: "mihir",
    name: "Md Muhibuzzaman",
    title: "Mortgage & Protection Adviser",
    specialties: ["Residential Mortgages", "Commercial Mortgages", "Buy-to-Let", "Protection"],
    image: "https://customer-assets.emergentagent.com/job_253ab87a-e10d-46f5-9b4d-bd4ec5274312/artifacts/0394egmt_Md%20Muhibuzzaman.png",
    phone: "07872614330",
    email: "mihir@thamesresi.com",
    bio: "Dedicated to finding the best mortgage solutions for families and investors. FCA regulated, with a passion for making home ownership accessible to everyone."
  },
  {
    id: "gill",
    name: "Gill",
    title: "Mortgage & Protection Adviser",
    specialties: ["Residential Mortgages", "Buy-to-Let", "Remortgage", "Protection"],
    image: null,
    phone: "07533662357",
    email: "gill@thamesresi.com",
    bio: "Committed to providing tailored mortgage advice with a personal touch. FCA regulated, helping clients navigate the mortgage process with confidence."
  }
];

export default function AdvisorsPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#020617] via-[#0f172a] to-[#020617] text-white">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition mb-6">
            <Home className="w-4 h-4" />
            Back to Home
          </Link>
          <h1 className="text-4xl font-bold mb-4">Our Mortgage Advisors</h1>
          <p className="text-slate-400 max-w-2xl mx-auto">
            Meet our team of expert mortgage advisors. Each advisor is FCA regulated and dedicated to finding the best mortgage solution for your needs.
          </p>
        </motion.div>

        {/* Advisors Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {advisors.map((advisor, index) => (
            <motion.div
              key={advisor.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-2xl overflow-hidden hover:border-blue-500/50 transition-all duration-300"
            >
              <div className="p-6">
                <div className="flex items-start gap-4 mb-4">
                  {advisor.image ? (
                    <img
                      src={advisor.image}
                      alt={advisor.name}
                      className="w-20 h-20 rounded-xl object-cover border-2 border-slate-600"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center flex-shrink-0 border-2 border-slate-600">
                      <User className="w-10 h-10 text-white/80" />
                    </div>
                  )}
                  <div>
                    <h2 className="text-xl font-bold">{advisor.name}</h2>
                    <p className="text-blue-400 text-sm">{advisor.title}</p>
                  </div>
                </div>

                <p className="text-slate-300 text-sm mb-4 leading-relaxed">{advisor.bio}</p>

                <div className="flex flex-wrap gap-2 mb-4">
                  {advisor.specialties.map((specialty) => (
                    <span
                      key={specialty}
                      className="px-2 py-1 bg-slate-700/50 rounded-full text-xs text-slate-300"
                    >
                      {specialty}
                    </span>
                  ))}
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm text-slate-400">
                    <Phone className="w-4 h-4" />
                    <a href={`tel:${advisor.phone}`} className="hover:text-white transition">{advisor.phone}</a>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-400">
                    <Mail className="w-4 h-4" />
                    <a href={`mailto:${advisor.email}`} className="hover:text-white transition">{advisor.email}</a>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Link to={`/bio/${advisor.id}`} className="flex-1">
                    <Button className="w-full bg-blue-600 hover:bg-blue-700">
                      View Profile <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                  <a href={`tel:${advisor.phone}`} className="flex-1">
                    <Button variant="outline" className="w-full border-slate-600 hover:bg-slate-700">
                      <Phone className="w-4 h-4 mr-2" /> Call
                    </Button>
                  </a>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-12 text-center"
        >
          <div className="bg-gradient-to-r from-blue-500/10 to-amber-500/10 border border-blue-500/30 rounded-2xl p-8">
            <h3 className="text-2xl font-bold mb-2">Not Sure Who to Contact?</h3>
            <p className="text-slate-400 mb-6">Book a consultation and we'll match you with the right advisor for your needs.</p>
            <Link to="/book-appointment">
              <Button className="bg-gradient-to-r from-blue-600 to-amber-600 hover:from-blue-700 hover:to-amber-700 px-8">
                <Calendar className="w-5 h-5 mr-2" />
                Book Consultation
              </Button>
            </Link>
          </div>
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mt-12 text-center"
        >
          <div className="flex items-center justify-center gap-2 text-slate-500 text-sm mb-2">
            <MapPin className="w-4 h-4" />
            Unit 4.1, Town Quay Wharf, Abbey Road, Barking, IG11 7BZ
          </div>
          <p className="text-xs text-slate-600">
            Thames Mortgage & Protection Ltd • FCA Regulated • FRN 1045227
          </p>
        </motion.div>
      </div>
    </div>
  );
}
